import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take, switchMap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../services/authentication.service';
import { CommonModule, DatePipe } from '@angular/common';
import { FileSizePipe } from './../../pipes/file-size.pipe';
import { MatButton } from '@angular/material/button';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { FileItemDto, DownloadApiService } from '../../../../../api/generated';
import { notNullOrUndefined, loadingObservable } from '../../../../../shared/utils';
import { MatDivider } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-download',
  templateUrl: './download.component.html',
  styleUrl: './download.component.scss',
  standalone: true,
  imports: [MatDivider, MatButton, MatCard, MatCardActions, MatCardContent, CommonModule, MatCardHeader, MatCardTitle, MatProgressSpinner, MatList, MatListItem, MatIcon, MatListItemIcon, MatListItemTitle, MatIconButton, DatePipe, FileSizePipe, TranslateModule]
})
export class DownloadComponent implements OnDestroy {
  loading: boolean = false;
  files: FileItemDto[] = [];
  guid: string = "";
  folderName: string = "";
  creatorName: string = "";
  downloadingAll: boolean = false;

  private redirectTimeout: any;

  constructor(
    private downloadService: DownloadApiService,
    private authService: AuthenticationService,
    private httpClient: HttpClient,
    private location: Location,
    private languageService: LanguageService
  ) {
    this.getFiles();
    this.getMap();
    // Set a timeout to redirect to the previous URL after 10 minutes (600,000 ms)
    this.redirectTimeout = setTimeout(() => {
      this.location.back();
    }, 600000);

    if(sessionStorage.getItem("language")) {
        this.languageService.setLanguage(sessionStorage.getItem("language")!);
      }
  }
  ngOnDestroy(): void {
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }
  getFiles() {
    const request$ = this.authService.authenticatedUser$
      .pipe(
        filter(notNullOrUndefined),
        take(1),
        switchMap(user => this.downloadService.getApiV10FilesDownloadGetFilesForPerson(user.id))
      );

    loadingObservable(request$)
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loading = response.loading;
          } else {
            this.files = response.items ?? [];
          }
        }
      });
  }

  downloadFile(file: FileItemDto): void {
    file.isDownloading = true;
    this.authService.authenticatedUser$
      .pipe(
        filter(notNullOrUndefined),
        take(1),
        switchMap(user => {
          // Note: using the HttpClient directly here because the generated api code does not support downloading files.
          // Could be fixed by adding a custom request file: https://github.com/ferdikoomen/openapi-typescript-codegen/blob/master/docs/custom-request-file.md#custom-request-file
          return this.httpClient.get(`${environment.api.basePath}/api/v1.0/Files/Download/DownloadFile/${user.id}/${file.id}`, {
            responseType: 'blob',
          })
        })
      ).subscribe({
        next: (response) => {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(response);
          link.download = file.name;
          link.click();
          file.isDownloading = false;
        }
      });
  }

  getMap() {
    this.authService.authenticatedUser$
      .pipe(
        filter(notNullOrUndefined),
        takeUntilDestroyed()
      )
      .subscribe(user => {
        this.folderName = user.folderName;
        this.creatorName = user.creatorName;
      });
  }

  getIcon(fileName: string | undefined): string {
    if (!fileName) {
      return 'fa-solid fa-file-lines'; // Default icon if fileName is undefined
    }

    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension) {
      return 'fa-solid fa-file-lines'; // Default if no extension
    }

    switch (extension) {
      case 'pdf':
        return 'fa-solid fa-file-pdf icon-red';
      case 'doc':
      case 'docx':
        return 'fa-solid fa-file-word icon-blue';
      case 'xls':
      case 'xlsx':
        return 'fa-solid fa-file-excel icon-green';
      case 'ppt':
      case 'pptx':
        return 'fa-solid fa-file-powerpoint icon-powerpoint';
      case 'mp3':
      case 'wav':
        return 'fa-solid fa-file-audio';
      case 'mp4':
      case 'avi':
        return 'fa-solid fa-file-video icon-purple';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return 'fa-solid fa-file-image icon-image';
      case 'zip':
      case 'rar':
        return 'fa-solid fa-file-archive icon-yellow';
      case 'txt':
        return 'fa-solid fa-file-alt icon-gray';
      case 'html':
      case 'css':
      case 'js':
      case 'php':
        return 'fa-solid fa-file-code icon-code';
      default:
        return 'fa-solid fa-file-lines icon-default'; // Default icon
    }
  }

  downloadAllFiles() {
    this.files.forEach(file => file.isDownloading = true);
    this.downloadingAll = true;
    this.authService.authenticatedUser$
      .pipe(
        filter(notNullOrUndefined),
        take(1),
        switchMap(user => {
          // Note: using the HttpClient directly here because the generated api code does not support downloading files.
          // Could be fixed by adding a custom request file: https://github.com/ferdikoomen/openapi-typescript-codegen/blob/master/docs/custom-request-file.md#custom-request-file
          return this.httpClient.get(`${environment.api.basePath}/api/v1.0/Files/Download/DownloadAllFiles/${user.id}`, {
            responseType: 'blob',
          })
        })
      ).subscribe({
        next: (response) => {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(response);
          link.download = this.folderName + '.zip';
          link.click();
          this.files.forEach(file => file.isDownloading = false);
          this.downloadingAll = false;
        },
        error: (error) => {
          this.files.forEach(file => file.isDownloading = false);
          this.downloadingAll = false;
        }
      });
  }
}
function ngOnDestroy() {
  throw new Error('Function not implemented.');
}

